// /live — Live tab 觀看入口（spec 16 §3.2）：選一台攝影機 → /devices/:id/live。
// 清單資料復用 devices 區域的 useDeviceList（同屬 F1 Devices+Live 範圍）。
// W2-02：degraded/offline（spec 10 §4.3）→「觀看」入口 disable（即時功能
// 需 WSS；REST 清單照常顯示）。
// WEB3-F1：「選一台攝影機」依 capability_effective 過濾（ADR-034 / 16 §3.2）——
// non-camera（如 sensor-th 僅 0x8000/0x8010）隱藏，避免點「觀看」後 start_viewing
// 30s timeout 白等；cold-start（capability 空，21 §2.4）顯示但 disable +
// 「等待裝置首次連線…」，不得誤判為非攝影機。

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DEGRADED_BANNER_TEXT, useConnectionMode } from '../../lib/connection';
import { formatRelativeUs } from '../../lib/format';
import { CAPABILITY_PENDING_TEXT, TILE_STATE_LABEL, cameraClass, deviceName, tileState } from '../devices/deviceModel';
import { useDeviceList } from '../devices/useDeviceList';
import '../devices/devices.css';

export default function LiveHubPage() {
  const { t } = useTranslation();
  const { devices, loading, error, reload } = useDeviceList();
  const liveDisabled = useConnectionMode() !== 'online';
  // camera + cold-start unknown 入列；non-camera 隱藏（「選一台攝影機」語意）。
  const cameras = devices?.filter((d) => cameraClass(d) !== 'non-camera') ?? null;

  return (
    <section>
      <header className="dv-page-head">
        <h1>{t('live.hubTitle')}</h1>
        <p className="text-secondary"><Link to="/live/grid">{t('live.gridNavLink')}</Link></p>
        <button className="btn-ghost" onClick={() => void reload()} disabled={loading}>
          {loading ? t('common.loading') : t('common.refresh')}
        </button>
      </header>

      {loading && !devices && (
        <div className="lv-hub-list" aria-label={t('common.loading')}>
          {[0, 1, 2].map((i) => (
            <div key={i} className="card lv-hub-row dv-skeleton">
              <div className="dv-skel-line" style={{ flex: 1 }} />
              <div className="dv-skel-line" style={{ width: 72 }} />
            </div>
          ))}
        </div>
      )}

      {error && !loading && (
        <div className="card dv-state-card">
          <p className="error-text" style={{ margin: 0 }}>
            {t('live.deviceListLoadFailed', { error })}
          </p>
          <button className="btn-primary" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!loading && !error && cameras && cameras.length === 0 && (
        <div className="card dv-state-card">
          <h2 style={{ margin: 0 }}>{t('live.noCamerasTitle')}</h2>
          <p className="text-secondary" style={{ margin: 0 }}>
            {t('live.noCamerasBody')}
          </p>
        </div>
      )}

      {!error && cameras && cameras.length > 0 && (
        <div className="lv-hub-list">
          {cameras.map((d) => {
            const st = tileState(d);
            const pending = cameraClass(d) === 'unknown'; // cold-start（21 §2.4）
            return (
              <div key={d.device_id} className="card lv-hub-row">
                <span className={`dv-dot dv-dot-${st}`} />
                <span className="dv-name">{deviceName(d)}</span>
                <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
                  {pending ? CAPABILITY_PENDING_TEXT : TILE_STATE_LABEL[st]}
                  {!pending && st === 'offline' && d.online?.last_seen != null ? `・${formatRelativeUs(d.online.last_seen)}` : ''}
                </span>
                {pending ? (
                  <button className="btn-ghost" disabled title={CAPABILITY_PENDING_TEXT}>
                    {t('live.watch')}
                  </button>
                ) : st === 'offline' ? (
                  <button className="btn-ghost" disabled title={t('live.deviceOffline')}>
                    {t('live.watch')}
                  </button>
                ) : liveDisabled ? (
                  <button className="btn-ghost" disabled title={DEGRADED_BANNER_TEXT}>
                    {t('live.watch')}
                  </button>
                ) : (
                  <Link className="btn-primary" to={`/devices/${encodeURIComponent(d.device_id)}/live`}>
                    {t('live.watch')}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
